/**
 * Enhanced Variant Picker - Compatible with Global PubSub
 * This version works harmoniously with the cart drawer pubsub
 */

class EnhancedVariantPicker extends HTMLElement {
  constructor() {
    super();
    this.debugMode = false;
    // console.log('EnhancedVariantPicker constructor called');
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  debug(...args) {
    if (this.debugMode) {
      console.log('[EnhancedVariantPicker]', ...args);
    }
  }

  init() {
    this.debug('Initializing');
    
    this.productForm = this.closest('product-form') || this.closest('form');
    this.debug('Product form found:', this.productForm);
    
    this.formId = this.dataset.formId || this.dataset.section;
    this.debug('Form ID:', this.formId);
    
    this.variantIdInput = document.querySelector(`#product-form-${this.formId} input[name="id"]`);
    this.debug('Variant ID input found:', this.variantIdInput);
    
    // Find media gallery components
    this.mediaGallery = document.querySelector(`#MediaGallery-${this.dataset.section}`);
    this.thumbnails = document.querySelectorAll('[data-thumbnail]');
    this.mediaItems = document.querySelectorAll('[data-media-id]');
    
    this.debug('Media elements found:', {
      gallery: this.mediaGallery,
      thumbnails: this.thumbnails.length,
      items: this.mediaItems.length
    });
    
    this.radios = this.querySelectorAll('input[type="radio"]');
    this.debug('Radio buttons found:', this.radios.length);
    
    this.optionLabels = {};

    // Initialize option labels
    this.querySelectorAll('.option-container').forEach((container, index) => {
      const optionIndex = index + 1;
      const labelElement = container.querySelector('.form__label');
      if (labelElement) {
        this.optionLabels[`option${optionIndex}`] = labelElement;
      }
    });
    this.debug('Option labels initialized:', this.optionLabels);

    // Initial variant selection
    this.updateVariantState();
    
    // Add event listeners
    this.radios.forEach(radio => {
      radio.addEventListener('change', this.onVariantChange.bind(this));
    });

    // Listen for variant change events from other components
    document.addEventListener('variant:change', this.onVariantChangeEvent.bind(this));
    
    // Also listen using PubSub if available
    if (window.subscribe && window.PUB_SUB_EVENTS) {
      try {
        window.subscribe(window.PUB_SUB_EVENTS.variantChange, (data) => {
          this.onVariantChangeEvent({ detail: data });
        });
      } catch (e) {
        this.debug('Could not subscribe to PubSub events:', e);
      }
    }

    // Debug: Log initial state
    this.debug('Initial variant data:', this.getVariantData());
  }

  onVariantChange(event) {
    if (!event.target) return;

    const selectedVariant = this.getSelectedVariant();
    if (!selectedVariant) return;

    this.debug('Variant changed to:', selectedVariant);

    // Update state and form
    this.updateVariantState();
    this.updateFormVariantId();

    // Get media ID from variant
    const mediaId = this.getVariantMediaId(selectedVariant);
    this.debug('Variant media ID:', mediaId);

    if (mediaId) {
      // Update media through Dawn's expected method
      this.updateProductMedia(mediaId, selectedVariant);
    }

    // Dispatch variant change events
    this.dispatchVariantChangeEvents(selectedVariant);

    // Update URL
    this.updateURL(selectedVariant);
  }

  getVariantMediaId(variant) {
    // Try different properties where media ID might be stored
    const mediaId = variant.featured_media?.id || 
                   variant.featured_image?.id || 
                   variant.media?.[0]?.id ||
                   variant.image_id;
    
    this.debug('Found media ID:', mediaId, 'for variant:', variant);
    return mediaId;
  }

  updateProductMedia(mediaId, variant) {
    this.debug('Updating product media with ID:', mediaId);

    // Find the media gallery component
    const mediaGallery = document.querySelector('media-gallery');
    if (!mediaGallery) {
      this.debug('Media gallery component not found');
      return;
    }

    const galleryViewer = document.querySelector(`#GalleryViewer-${this.dataset.section}`);
    const gallerySlider = document.querySelector(`#Slider-Gallery-${this.dataset.section}`);

    // If we found a media element, update it
    if (gallerySlider) {
      const mediaItems = gallerySlider.querySelectorAll('.product__media-item');
      this.debug('Found media items:', mediaItems.length);

      mediaItems.forEach(item => {
        const itemMediaId = item.dataset.mediaId?.split('-').pop() || item.id.split('-').pop();
        
        item.classList.remove('is-active');
        if (itemMediaId === mediaId.toString()) {
          this.debug('Setting media item active:', itemMediaId);
          item.classList.add('is-active');
          
          // Try to scroll the item into view if needed
          if (typeof item.scrollIntoView === 'function') {
            try {
              item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            } catch (e) {
              this.debug('Error scrolling to item:', e);
            }
          }
        }
      });

      // Dispatch events to notify Dawn's media gallery
      const templateId = this.dataset.section;
      const events = [
        new CustomEvent('product:media:change', {
          detail: {
            variant,
            mediaId: `${templateId}-${mediaId}`,
            sectionId: templateId
          },
          bubbles: true
        }),
        new CustomEvent('variant:change', {
          detail: {
            variant,
            sectionId: templateId
          },
          bubbles: true
        })
      ];

      events.forEach(event => {
        this.debug('Dispatching event:', event.type, event.detail);
        mediaGallery.dispatchEvent(event);
        document.dispatchEvent(event);
        
        // Also dispatch to the viewer component
        if (galleryViewer) {
          galleryViewer.dispatchEvent(event);
        }
      });
    } else {
      this.debug('Gallery slider not found');
    }
  }

  dispatchVariantChangeEvents(variant) {
    const eventDetail = { 
      variant,
      sectionId: this.dataset.section
    };
    
    // Method 1: Use CustomEvent (always works)
    const event = new CustomEvent('variant:change', {
      detail: eventDetail,
      bubbles: true,
      cancelable: false
    });
    
    this.debug('Dispatching variant:change event:', eventDetail);
    this.dispatchEvent(event);
    document.dispatchEvent(event);
    
    // Method 2: Use PubSub if available
    if (window.publish && window.PUB_SUB_EVENTS) {
      try {
        window.publish(window.PUB_SUB_EVENTS.variantChange, eventDetail);
      } catch (e) {
        this.debug('Could not publish to PubSub:', e);
      }
    }
  }

  updateURL(variant) {
    if (this.dataset.updateUrl === 'true' && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({ path: url.toString() }, '', url.toString());
      this.debug('Updated URL:', url.toString());
    }
  }

  onVariantChangeEvent(event) {
    if (!event.detail?.variant) return;
    
    const variant = event.detail.variant;
    
    // Update radio button selection
    variant.options.forEach((optionValue, index) => {
      const optionPosition = index + 1;
      const radio = this.querySelector(
        `input[data-index="option${optionPosition}"][value="${this.escapeQuotes(optionValue)}"]`
      );
      if (radio && !radio.checked) {
        radio.checked = true;
      }
    });
    
    this.updateVariantState();
  }

  escapeQuotes(str) {
    return str.replace(/"/g, '\\"').replace(/'/g, "\\'");
  }

  updateVariantState() {
    // For each option, update the selected value display
    Object.keys(this.optionLabels).forEach(optionKey => {
      const selectedRadio = this.querySelector(`input[data-index="${optionKey}"]:checked`);
      const label = this.optionLabels[optionKey];
      
      if (selectedRadio && label) {
        // Find or create selected value display span
        let valueDisplay = label.querySelector('.selected-value-display');
        if (!valueDisplay) {
          valueDisplay = document.createElement('span');
          valueDisplay.className = 'selected-value-display';
          label.appendChild(valueDisplay);
        }
        
        valueDisplay.textContent = ` - ${selectedRadio.value}`;
      }
    });
    
    // Update disabled state of options based on available variants
    this.updateOptionAvailability();
  }

  updateOptionAvailability() {
    const variantData = this.getVariantData();
    if (!variantData.length) return;
    
    // Get currently selected options
    const selectedOptions = this.getSelectedOptions();
    
    // For each option position, determine which values are available
    const optionPositions = Object.keys(this.optionLabels).map(key => 
      parseInt(key.replace('option', ''))
    );
    
    optionPositions.forEach(position => {
      const optionIndex = position - 1;
      const availableOptionValues = this.getAvailableOptionValues(
        optionIndex, 
        selectedOptions, 
        variantData
      );
      
      // Update availability of option values
      const optionRadios = this.querySelectorAll(`input[data-index="option${position}"]`);
      optionRadios.forEach(radio => {
        const optionValue = radio.value;
        const isAvailable = availableOptionValues.includes(optionValue);
        const container = radio.closest('.variant-button-item, .variant-thumbnail-item');
        
        if (container) {
          if (isAvailable) {
            container.classList.remove('disabled');
            radio.disabled = false;
          } else {
            container.classList.add('disabled');
            radio.disabled = true;
          }
        }
      });
    });
  }

  getAvailableOptionValues(optionIndex, selectedOptions, variants) {
    const availableValues = new Set();
    
    variants.forEach(variant => {
      // Check if this variant matches the currently selected options
      // excluding the option we're checking availability for
      let isMatch = true;
      
      Object.keys(selectedOptions).forEach(optionPosition => {
        const position = parseInt(optionPosition.replace('option', '')) - 1;
        
        // Skip the option we're checking
        if (position === optionIndex) return;
        
        // Check if this option matches the selected value
        if (variant.options[position] !== selectedOptions[optionPosition]) {
          isMatch = false;
        }
      });
      
      // If all other options match, this option value is available
      if (isMatch && variant.available) {
        availableValues.add(variant.options[optionIndex]);
      }
    });
    
    return Array.from(availableValues);
  }

  getSelectedOptions() {
    const selectedOptions = {};
    
    Object.keys(this.optionLabels).forEach(optionKey => {
      const selectedRadio = this.querySelector(`input[data-index="${optionKey}"]:checked`);
      if (selectedRadio) {
        selectedOptions[optionKey] = selectedRadio.value;
      }
    });
    
    return selectedOptions;
  }

  getSelectedVariant() {
    const variantData = this.getVariantData();
    if (!variantData.length) return null;
    
    const selectedOptions = this.getSelectedOptions();
    const optionKeys = Object.keys(selectedOptions);
    
    // Find the variant that matches all selected options
    return variantData.find(variant => {
      return optionKeys.every(key => {
        const position = parseInt(key.replace('option', '')) - 1;
        return variant.options[position] === selectedOptions[key];
      });
    });
  }

  updateFormVariantId() {
    const selectedVariant = this.getSelectedVariant();
    
    if (selectedVariant && this.variantIdInput) {
      this.variantIdInput.value = selectedVariant.id;
    }
  }

  getVariantData() {
    let variantData = [];
    
    const productJson = document.getElementById(`ProductJson-${this.dataset.section}`);
    if (productJson) {
      try {
        variantData = JSON.parse(productJson.textContent);
        this.debug('Parsed variant data:', variantData);
      } catch (e) {
        console.error('Error parsing product JSON:', e);
      }
    }
    
    return variantData;
  }
}

// Define the custom element
if (!customElements.get('enhanced-variant-picker')) {
  // console.log('Registering enhanced-variant-picker custom element');
  customElements.define('enhanced-variant-picker', EnhancedVariantPicker);
}

// console.log('Enhanced variant picker script loaded');